import React from "react";
import {
  Paper,
  Stack,
  Typography,
  useTheme,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
} from "@mui/material";
import WavesIcon from "@mui/icons-material/Waves";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useUnits } from "../../contexts/UnitContext"; // Make sure path is correct

export const TideTableCompact = ({
  stationName,
  allTideEvents,
  nextTide,
  toggleExpanded,
}) => {
  const theme = useTheme();
  const { unitSystem, convert } = useUnits();

  // Use the convert utility from UnitContext for height conversion
  const convertHeight = (height) => {
    if (unitSystem === "metric") {
      return (height * 0.3048).toFixed(2); // Convert ft to m
    }
    return height.toFixed(2);
  };

  // Get the appropriate unit label
  const getUnitLabel = () => {
    return unitSystem === "metric" ? "m" : "ft";
  };

  // Use the time formatter from UnitContext
  const formatTime = (time) => {
    if (!time) return "";
    return convert.time.format(time);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        width: "100%",
        cursor: "pointer",
        "&:hover": {
          bgcolor: theme.palette.action.hover,
        },
        mb: 2,
      }}
      onClick={toggleExpanded}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <WavesIcon color="primary" />
          <Typography variant="subtitle1" color="primary">
            Tide Schedule
          </Typography>
        </Stack>

        {nextTide && (
          <Chip
            label={`Next: ${nextTide.type} tide at ${formatTime(
              nextTide.time
            )}`}
            size="small"
            color={nextTide.type === "High" ? "success" : "error"}
            variant="outlined"
          />
        )}

        <IconButton onClick={toggleExpanded} size="small">
          <CompareArrowsIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ mt: 1 }}>
        <TableContainer component={Box}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="right">Height ({getUnitLabel()})</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTideEvents.slice(0, 3).map((item, index) => (
                <TableRow key={`tide-event-compact-${index}`}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={
                        item.type === "High" ? "success.main" : "error.main"
                      }
                    >
                      {item.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatTime(item.time)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {convertHeight(item.height)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {stationName?.name && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block", textAlign: "center", mt: 0.5 }}
        >
          {stationName.name}
        </Typography>
      )}
    </Paper>
  );
};
